<template>
  <Pane :value="isOpen"
        :tabs="tabs"
        :current-tab="currentTab"
        location="left"
        temporary
        data-locator="signed-documents-filters-pane"
        @closeButtonClicked="resetFilters"
  >
    <template #content-filters>
      <div class="text-body-1 font-weight-bold mb-4">
        {{ $t('project.signed-documents.pane.SignedDocumentsFiltersPane.dates') }}
      </div>
      <AppDateRangePicker
        :model-value="datesFilter"
        :label="$t('project.signed-documents.pane.SignedDocumentsFiltersPane.inputSignDateLabel')"
        @update:model-value="handleDatesFilterChange"
      />

      <template v-if="signatories.length > 0">
        <v-divider class="my-6"/>

        <div class="text-body-1 font-weight-bold">
          {{ $t('project.signed-documents.pane.SignedDocumentsFiltersPane.signatories') }}
        </div>
        <v-row no-gutters>
          <v-col
            v-for="signatory in signatories.length > 10 && !displayMoreSignatories ? shortSignatories : signatories"
            :key="`signatory-${signatory.signerId}`"
            class="py-1"
            cols="12"
            md="6"
          >
            <v-checkbox :model-value="filters.signatories"
                        :label="signatory.fullName"
                        density="compact"
                        hide-details
                        class="SignedDocumentsFiltersPane-checkbox my-1 py-0"
                        :value="signatory.signerId"
                        @update:model-value="setFiltersSignatories($event)"
            />
          </v-col>
        </v-row>

        <h4 v-if="signatories.length > 10 && !displayMoreSignatories"
            class="displayMoreSignatories clickable"
            @click="displayMoreSignatories = true"
        >
          {{ $t('project.signed-documents.pane.SignedDocumentsFiltersPane.displayMoreSignatories') }}
        </h4>

        <h4 v-if="signatories.length > 10 && displayMoreSignatories"
            class="displayLessSignatories clickable"
            @click="displayMoreSignatories = false"
        >
          {{ $t('project.signed-documents.pane.SignedDocumentsFiltersPane.displayLessSignatories') }}
        </h4>
      </template>

      <v-divider class="mt-4 mb-6"/>

      <v-row>
        <v-col>
          <AppButton block data-locator="clear-button" @click="resetFilters">
            {{ $t('common.clear') }}
          </AppButton>
        </v-col>
        <v-col>
          <AppButton color="primary"
                     block
                     data-locator="apply-button"
                     @click="closePane"
          >
            {{ $t('common.apply') }}
          </AppButton>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import Pane from '@/common/Pane.vue'

export default {
  name: 'SignedDocumentsFiltersPane',
  components: { AppDateRangePicker, Pane, AppButton },
  data () {
    return {
      tabs: [
        {
          title: this.$t('project.signed-documents.pane.SignedDocumentsFiltersPane.filters'),
          id: 'filters',
        },
      ],
      currentTab: 'filters',
      displayMoreSignatories: false,
    }
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState('signedDocuments', ['signedDocuments', 'filters']),
    datesFilter() {
      const { startDate, endDate } = this.filters
      if (startDate) {
        return endDate ? [startDate, endDate] : [startDate]
      }

      return []
    },
    signatories() {
      const array = []
      this.signedDocuments.forEach(signedDocument => {
        if (signedDocument?.mySignature) {
          if (!array.find(signatory => signatory.signerId === signedDocument.mySignature.signerId)) {
            array.push(signedDocument.mySignature)
          }
        }
        if (signedDocument?.otherSignatures?.length > 0) {
          signedDocument.otherSignatures.forEach(otherSignature => {
            if (!array.find(signatory => signatory.signerId === otherSignature.signerId)) {
              array.push(otherSignature)
            }
          })
        }
      })
      return array
    },
    shortSignatories() {
      return this.signatories.slice(0, 10)
    },
  },
  methods: {
    ...mapActions('signedDocuments', ['setFiltersStartDate', 'setFiltersEndDate', 'setFiltersSignatories']),
    closePane() {
      this.$emit('close')
    },
    handleDatesFilterChange(value) {
      this.setFiltersStartDate(value[0] || null)
      if (value.length === 2) {
        this.setFiltersEndDate(value[1])
      } else {
        this.setFiltersEndDate(null)
      }
    },
    resetFilters() {
      this.setFiltersSignatories([])
      this.setFiltersStartDate(null)
      this.setFiltersEndDate(null)
      this.closePane()
    },
  },
}
</script>

<style scoped lang="scss">
.displayMoreSignatories:hover,
.displayLessSignatories:hover {
  color: rgb(var(--v-theme-primary));
}

:deep(.SignedDocumentsFiltersPane-checkbox) {
  i {
    font-size: 18px;
  }

  label {
    font-size: 14px;
  }
}
</style>
